// <s-header> — sticky site header that hides while scrolling down and returns on scroll up.
// Exposes --header-offset on <html> (header height when visible, 0px when hidden) so sticky
// page elements can sit right under it. Stays visible near the top, while focus or the
// pointer is inside it (mega menu), and resets after a back/forward cache restore.
class SHeader extends HTMLElement {
  #ac;
  #lastY = 0;
  #ticking = false;
  #hovered = false;
  #resizeObserver;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };
    this.#lastY = window.scrollY;

    window.addEventListener('scroll', () => this.#onScroll(), opts);
    this.addEventListener('pointerenter', () => { this.#hovered = true; this.#show(); }, opts);
    this.addEventListener('pointerleave', () => { this.#hovered = false; }, opts);
    this.addEventListener('focusin', () => this.#show(), opts);
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      this.#lastY = window.scrollY;
      this.#show();
      for (const open of this.querySelectorAll('[data-open]')) open.removeAttribute('data-open');
    }, opts);

    this.#resizeObserver = new ResizeObserver(() => this.#publishOffset());
    this.#resizeObserver.observe(this);
    this.#publishOffset();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#resizeObserver?.disconnect();
  }

  #onScroll() {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      // A pinned body (open drawer) reports scrollY 0; ignore those frames.
      if (document.body.hasAttribute('data-scroll-locked')) return;
      const y = window.scrollY;
      const delta = y - this.#lastY;
      if (Math.abs(delta) < 8) return;
      const pastTop = y > this.offsetTop + this.offsetHeight + 48;
      if (delta > 0 && pastTop && !this.#hovered && !this.contains(document.activeElement)) {
        this.#hide();
      } else if (delta < 0 || !pastTop) {
        this.#show();
      }
      this.#lastY = y;
    });
  }

  #hide() {
    if (this.hasAttribute('data-hidden')) return;
    this.setAttribute('data-hidden', '');
    this.#publishOffset();
  }

  #show() {
    if (!this.hasAttribute('data-hidden')) return;
    this.removeAttribute('data-hidden');
    this.#publishOffset();
  }

  #publishOffset() {
    const offset = this.hasAttribute('data-hidden') ? 0 : this.offsetHeight;
    document.documentElement.style.setProperty('--header-offset', `${offset}px`);
  }
}

customElements.define('s-header', SHeader);
